import { Building2 } from "lucide-react";

import { useStaffOrg } from "@/contexts/StaffOrgContext";

/**
 * The org switcher for DigaVouch staff. Renders nothing for anyone the server
 * did not report as staff, which is every customer operator.
 *
 * A native `<select>`: it opens the platform's own picker on a tablet, needs
 * no portal, and is announced correctly by screen readers. The options are
 * what `/operator/me` listed as switchable (the environment's scope), with the
 * person's own org first when it is in scope. Choosing one calls the server;
 * the page then lands on `/` for the new org (see `StaffOrgContext`).
 */
export function StaffOrgSwitcher({ className = "" }: { className?: string }) {
  const staff = useStaffOrg();
  if (!staff || !staff.isStaff) return null;

  const { switchableOrgs, homeOrgId, selection, switching, switchTo, error } = staff;
  const home = switchableOrgs.find((o) => o.is_home) ?? null;
  const others = switchableOrgs.filter((o) => !o.is_home).sort((a, b) => a.name.localeCompare(b.name));
  // The value is the org being viewed: the selection while switched, else home.
  // A home org outside the scope (production) still needs an option to return to.
  const current = selection ?? homeOrgId ?? "";
  const homeLabel = home ? `${home.name} (your org)` : "Your organization";

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
        <Building2 className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="sr-only">Organization to view</span>
        <select
          aria-label="Organization to view"
          className="h-9 max-w-[16rem] rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
          value={current}
          disabled={switching}
          onChange={(e) => {
            const next = e.target.value;
            if (next === current) return;
            // Choosing the home org is "switch back": the server records it
            // and the selection header stops being sent.
            void switchTo(homeOrgId && next === homeOrgId ? null : next);
          }}
        >
          {homeOrgId ? <option value={homeOrgId}>{homeLabel}</option> : <option value="">Choose an organization</option>}
          {others.map((o) => (
            <option key={o.org_id} value={o.org_id}>
              {o.name}
            </option>
          ))}
        </select>
      </label>
      {error && (
        <p role="alert" className="text-xs text-trust-amber">
          {error}
        </p>
      )}
    </div>
  );
}
